import type { AutocompleteRenderValueGetItemProps } from "@mui/material";
import { Autocomplete, Box, Checkbox, Chip, TextField } from "@mui/material";
import { useLayoutEffect, useRef, useState } from "react";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader.tsx";
import type { ColumnOption } from "./blankFieldSettings.ts";

interface Props {
  options: ColumnOption[];
  value: ColumnOption[];
  loading?: boolean;
  disabled: boolean;
  onChange: (ids: string[]) => void;
}

const MAX_CHIP_LINES = 2;

const autocompleteSx = {
  "&:not(.Mui-focused) .MuiAutocomplete-inputRoot .MuiAutocomplete-input": { minWidth: 0, px: 0 },
};
const slotProps = {
  listbox: { sx: { "& .MuiAutocomplete-option": { py: 0.5, px: 1.5, height: 32, minHeight: 32 } } },
};
const optionCheckboxSx = { p: 0, mr: 1 };

const getOverflowLineTop = (firstChip: HTMLElement): number =>
  firstChip.offsetTop + MAX_CHIP_LINES * firstChip.offsetHeight;

const countFittingChips = (firstChip: HTMLElement, visibleCount: number): number => {
  const chips = Array.from(firstChip.parentElement?.querySelectorAll<HTMLElement>("[data-item-index]") ?? []);
  const firstOverflowingChip = chips.findIndex((chip) => chip.offsetTop >= getOverflowLineTop(firstChip));
  return firstOverflowingChip === -1 ? visibleCount - 1 : firstOverflowingChip;
};

interface FittedChips {
  layoutKey: string;
  count: number;
}

interface SelectedColumnChipsProps {
  columns: ColumnOption[];
  getItemProps: AutocompleteRenderValueGetItemProps<true>;
  disabled: boolean;
  focused: boolean;
  fittedChips?: FittedChips;
  onFit: (fittedChips: FittedChips) => void;
}

const SelectedColumnChips = ({
  columns,
  getItemProps,
  disabled,
  focused,
  fittedChips,
  onFit,
}: SelectedColumnChipsProps) => {
  const firstChipRef = useRef<HTMLDivElement>(null);
  const layoutKey = JSON.stringify([disabled, focused, columns.map((column) => column.label)]);
  const visibleCount = fittedChips?.layoutKey === layoutKey ? fittedChips.count : columns.length;

  useLayoutEffect(() => {
    const firstChip = firstChipRef.current;
    const input = firstChip?.parentElement?.querySelector("input");
    if (firstChip && input && visibleCount > 1 && input.offsetTop >= getOverflowLineTop(firstChip)) {
      onFit({ layoutKey, count: countFittingChips(firstChip, visibleCount) });
    }
  }, [layoutKey, visibleCount, onFit]);

  const hiddenCount = columns.length - visibleCount;

  return (
    <>
      {columns.slice(0, visibleCount).map((column, index) => {
        const { key, ...itemProps } = getItemProps({ index });
        return <Chip key={key} ref={index === 0 ? firstChipRef : undefined} label={column.label} {...itemProps} />;
      })}
      {hiddenCount > 0 && <Chip label={`+${hiddenCount}`} className="MuiAutocomplete-tag" disabled={disabled} />}
    </>
  );
};

const ColumnsAutocomplete = ({ options, value, loading, disabled, onChange }: Props) => {
  const [fittedChips, setFittedChips] = useState<FittedChips>();

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      sx={autocompleteSx}
      slotProps={slotProps}
      loading={loading}
      loadingText={<InlineLoader />}
      options={loading ? [] : options}
      value={value}
      getOptionLabel={(column) => column.label}
      getOptionKey={(column) => column.id}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onChange={(_, columns) => onChange(columns.map((column) => column.id))}
      disabled={disabled}
      renderInput={(params) => <TextField {...params} label="Pick columns" />}
      renderValue={(columns, getItemProps, { focused }) => (
        <SelectedColumnChips
          columns={columns}
          getItemProps={getItemProps}
          disabled={disabled}
          focused={focused}
          fittedChips={fittedChips}
          onFit={setFittedChips}
        />
      )}
      renderOption={({ key, ...props }, column, { selected }) => (
        <Box component="li" key={key} {...props}>
          <Checkbox checked={selected} tabIndex={-1} sx={optionCheckboxSx} />
          {column.label}
        </Box>
      )}
    />
  );
};

export default ColumnsAutocomplete;
